import { formatWallClock, localDayKey, type ArbZgFlag } from '@wf/shared'
import { formatDateDE } from '../time/format'

/** Kompaktes Stunden:Minuten-Label (z. B. „11:00 h") aus Sekunden. Auf Gesamt-Minuten runden (kein „:60"-Überlauf). */
const hm = (sec: number): string => {
  const totalMin = Math.round(sec / 60)
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, '0')} h`
}

/**
 * Klartext eines Arbeitszeit-Verstoßes (der Motor liefert die Zahlen, hier nur die Formulierung).
 * jugend_* = JArbSchG (unter 18, am Alter des Schichttags), azubi_* = BBiG (am Ausbildungsvertrag,
 * altersunabhängig), muschg_* = MuSchG.
 *
 * Bewusst eine exhaustive Record-Map statt if-Kette mit Fallback: der frühere bedingungslose
 * `return 'Ruhezeit unter 11 h'` am Ende hätte JEDES neue Flag als falschen § 5-ArbZG-Verstoß angezeigt.
 * Jetzt bricht der Build, solange ein Typ fehlt.
 */
const FLAG_TEXT = {
  over_10h: (f) => `Über 10 h Arbeitszeit (${hm(f.workedSeconds ?? 0)})`,
  break_short: (f) => `Pause zu kurz: ${Math.round((f.actualBreakSeconds ?? 0) / 60)} statt ${Math.round((f.requiredBreakSeconds ?? 0) / 60)} min nötig`,
  stretch_over_6h: (f) => `Über 6 h am Stück ohne Pause (${hm(f.stretchSeconds ?? 0)})`,
  rest_short: (f) => `Ruhezeit unter 11 h (${hm(f.restSeconds ?? 0)})`,
  jugend_over_8h: (f) => `Unter 18: Tagesarbeitszeit über der Grenze (${hm(f.workedSeconds ?? 0)})`,
  jugend_break_short: (f) => `Unter 18: Pause zu kurz: ${Math.round((f.actualBreakSeconds ?? 0) / 60)} statt ${Math.round((f.requiredBreakSeconds ?? 0) / 60)} min nötig`,
  jugend_stretch: (f) => `Unter 18: über 4,5 h am Stück ohne Pause (${hm(f.stretchSeconds ?? 0)})`,
  jugend_rest_short: (f) => `Unter 18: tägliche Freizeit unter 12 h (${hm(f.restSeconds ?? 0)})`,
  jugend_nachtarbeit: () => 'Unter 18: Beschäftigung außerhalb 6–20 Uhr',
  jugend_schichtzeit_over_10h: (f) => `Unter 18: Schichtzeit über 10 h (${hm(f.workedSeconds ?? 0)} von Beginn bis Ende, inkl. Pausen)`,
  muschg_over_8_5h: (f) => `Mutterschutz: über 8,5 h Tagesarbeitszeit (${hm(f.workedSeconds ?? 0)})`,
  muschg_nachtarbeit: () => 'Mutterschutz: Beschäftigung außerhalb 6–20 Uhr',
  jugend_week_over_40h: (f) => `Unter 18: über 40 h in der Woche (${hm(f.workedSeconds ?? 0)})`,
  jugend_week_over_5_days: (f) => `Unter 18: ${f.count ?? 0} Beschäftigungstage in der Woche (höchstens 5)`,
  jugend_samstage_im_monat: (f) => `Unter 18: ${f.count ?? 0} beschäftigte Samstage im Monat — zwei sollen frei bleiben`,
  jugend_sonntag: () => 'Unter 18: Sonntagsarbeit — für Kfz-Werkstätten gibt es keinen Ausnahmetatbestand',
  jugend_feiertag: () => 'Unter 18: Feiertagsarbeit — für Kfz-Werkstätten gibt es keinen Ausnahmetatbestand',
  azubi_schultag_beschaeftigt: () => 'Azubi: Betrieb nach einem ganztägigen Berufsschultag (gilt auch für Volljährige)',
  jugend_schultag_beschaeftigt: () => 'Unter 18: Betrieb nach einem ganztägigen Berufsschultag',
} satisfies Record<ArbZgFlag['type'], (f: ArbZgFlag) => string>

const flagText = (f: ArbZgFlag): string => FLAG_TEXT[f.type](f)

const FLAG_LAW = {
  over_10h: '§3 ArbZG',
  break_short: '§4 ArbZG',
  stretch_over_6h: '§4 ArbZG',
  rest_short: '§5 ArbZG',
  jugend_over_8h: '§8 JArbSchG',
  jugend_break_short: '§11 JArbSchG',
  jugend_stretch: '§11 JArbSchG',
  jugend_rest_short: '§13 JArbSchG',
  jugend_nachtarbeit: '§14 JArbSchG',
  jugend_schichtzeit_over_10h: '§12 JArbSchG',
  muschg_over_8_5h: '§4 MuSchG',
  muschg_nachtarbeit: '§5 MuSchG',
  jugend_week_over_40h: '§8 JArbSchG',
  jugend_week_over_5_days: '§15 JArbSchG',
  jugend_samstage_im_monat: '§16 JArbSchG',
  jugend_sonntag: '§17 JArbSchG',
  jugend_feiertag: '§18 JArbSchG',
  azubi_schultag_beschaeftigt: '§15 BBiG',
  jugend_schultag_beschaeftigt: '§9 JArbSchG',
} satisfies Record<ArbZgFlag['type'], string>

const flagLaw = (t: ArbZgFlag['type']): string => FLAG_LAW[t]

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const

/**
 * Zeitraum-Beschriftung. Ein Schicht-Flag trägt echte Uhrzeiten; ein WOCHEN- oder MONATS-Flag hat dagegen
 * nur synthetische Perioden-Grenzen (Montag 00:00 bzw. Monatserster) — „05.01.2026 00:00–11.01.2026 23:59"
 * würde dort eine Genauigkeit vortäuschen, die es nicht gibt, und sähe aus wie eine Schicht.
 */
function zeitraumLabel(f: ArbZgFlag): string {
  if (f.periodKind === 'week') return `Woche ab ${formatDateDE(f.periodKey ?? localDayKey(f.shiftStartMs))}`
  if (f.periodKind === 'month') {
    const ym = f.periodKey ?? localDayKey(f.shiftStartMs).slice(0, 7)
    return `Monat ${MONATE[Number(ym.slice(5, 7)) - 1] ?? ym.slice(5, 7)} ${ym.slice(0, 4)}`
  }
  if (f.periodKind === 'day') return formatDateDE(f.periodKey ?? localDayKey(f.shiftStartMs))
  return `${formatDateDE(localDayKey(f.shiftStartMs))} ${formatWallClock(f.shiftStartMs)}–${formatWallClock(f.shiftEndMs)}`
}

/**
 * ArbZG-Wächter (R4/1.9 + 1.10): listet die schicht-basierten Verstöße des Monats. Rein hinweisend — jeder Verstoß
 * als eigener Warnstreifen (Wiederverwendung des `wf-ztwarn`-Stils). Eine Anomalie-Schicht (vermutlich vergessenes
 * Ausstempeln) bekommt einen neutralen Zusatz statt eines harten Alarms. Mit Quittier-Recht (canAck) kann der Chef
 * einen Verstoß „gesehen/bearbeitet" markieren; ein bereits quittierter zeigt „quittiert" statt des Buttons.
 */
export function ArbZgFlagsCard({
  flags,
  canAck = false,
  onAck,
  acking = false,
  label = 'ArbZG-Verstöße',
}: {
  flags: ArbZgFlag[]
  canAck?: boolean
  onAck?: (f: ArbZgFlag) => void
  acking?: boolean
  /** Eigene Beschriftung je Karte — zwei Karten mit derselben Landmark wären für Screenreader und Tests
   *  nicht unterscheidbar, und „ArbZG-Verstöße" ist für die JArbSchG-Perioden ohnehin falsch. */
  label?: string
}) {
  if (flags.length === 0) return null
  return (
    <div role="region" aria-label={label} className="wf-zstack">
      {flags.map((f, i) => (
        <div key={`${f.type}-${f.shiftStartMs}-${i}`} className={f.advisory ? 'wf-ztwarn wf-ztwarn--hint' : 'wf-ztwarn'} role={f.advisory ? 'status' : 'alert'}>
          <span aria-hidden="true">{f.advisory ? 'ℹ' : '⚖'}</span>
          <span>
            {zeitraumLabel(f)}: {flagText(f)} ({flagLaw(f.type)}){f.advisory ? ' — Soll-Vorschrift, kein Verstoß' : ''}
            {f.partial ? ' — Teilzeitraum, die Summe kann unvollständig sein' : ''}
            {f.anomaly ? ' — evtl. Ausstempeln vergessen, bitte prüfen' : ''}
          </span>
          {f.acked ? (
            <span className="wf-tag">quittiert</span>
          ) : canAck && onAck ? (
            <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => onAck(f)} disabled={acking}>
              Quittieren
            </button>
          ) : null}
        </div>
      ))}
    </div>
  )
}
